import { AppFilter } from '../../../redux/filter-ts/filterModels'
import uuid from '../../compositions/GroupedCheckboxes/uuid'
import { BaseScheduleFormData } from '../../entities/BaseSchedule/BaseSchedule'
import {
    deleteBaseScheduleApiCall,
    deleteBaseScheduleCommentApiCall,
    getBaseScheduleApiCall,
    getBaseSchedulesIndexApiCall,
    patchBaseScheduleApiCall,
    postBaseScheduleApiCall,
    postBaseScheduleCommentApiCall,
} from '../../entities/BaseSchedule/BaseScheduleService'
import { isFetchCollectionResultSuccessful, isFetchResultSuccessful } from '../../entities/FetchResult'
import { getCheckedFormOptionsValue } from '../../helpers/types/formOption'
import { Reducers } from '../reducers'
import { TypedDispatch } from '../store'
import {
    baseScheduleUpdated,
    commentAdded,
    commentDeleted,
    removeBaseScheduleFromIndex,
    setBaseSchedule,
    setBaseSchedules,
    setError,
    setIsEditSuccessful,
    setIsLoading,
    setIsSuccessful,
} from './baseScheduleReducer'

export const fetchBaseSchedules = (
    filters: AppFilter,
) => async (dispatch: TypedDispatch): Promise<void> => {
    dispatch(setIsLoading(true))
    dispatch(setError(''))

    const departmentIds = getCheckedFormOptionsValue(filters?.departmentOptions || [])

    try {
        const response = await getBaseSchedulesIndexApiCall(departmentIds)

        if (! isFetchCollectionResultSuccessful(response)) {
            dispatch(setError(response.error))

            return
        }

        dispatch(setBaseSchedules(response.data))
    } catch (error) {
        console.error('[fetchBaseSchedules]', error)
    } finally {
        dispatch(setIsLoading(false))
    }
}

export const getBaseSchedule = (id: string, showLoading = true) => async (dispatch: TypedDispatch): Promise<void> => {
    if (showLoading) {
        dispatch(setIsLoading(true))
    }
    dispatch(setError(''))

    try {
        const response = await getBaseScheduleApiCall(id)

        if (! isFetchResultSuccessful(response)) {
            dispatch(setError(response.error))

            return
        }

        dispatch(setBaseSchedule(response.data))
    } catch (error) {
        console.error('[getBaseSchedule]', error)
    } finally {
        if (showLoading) {
            dispatch(setIsLoading(false))
        }
    }
}

export const createBaseSchedule = (formData: BaseScheduleFormData) => async (dispatch: TypedDispatch): Promise<void> => {
    dispatch(setIsLoading(true))
    dispatch(setError(''))

    try {
        const response = await postBaseScheduleApiCall(formData)

        if (! isFetchResultSuccessful(response)) {
            dispatch(setError(response.error))

            return
        }

        const detailResponse = await getBaseScheduleApiCall(response.data.id)

        if (isFetchResultSuccessful(detailResponse)) {
            dispatch(setBaseSchedule(detailResponse.data))
        }

        dispatch(setIsSuccessful(true))
    } catch (error) {
        console.error('[createBaseSchedule]', error)
    } finally {
        dispatch(setIsLoading(false))
    }
}

export const editBaseSchedule = (
    baseScheduleId: string,
    formData: Partial<BaseScheduleFormData>,
) => async (dispatch: TypedDispatch): Promise<void> => {
    dispatch(setIsLoading(true))
    dispatch(setError(''))

    try {
        const response = await patchBaseScheduleApiCall(baseScheduleId, formData)

        if (! isFetchResultSuccessful(response)) {
            dispatch(setError(response.error))

            return
        }

        dispatch(baseScheduleUpdated(response.data))
        dispatch(setIsEditSuccessful(true))
    } catch (error) {
        console.error('[editBaseSchedule]', error)
    } finally {
        dispatch(setIsLoading(false))
    }
}

export const deleteBaseSchedule = (baseScheduleId: string) => async (dispatch: TypedDispatch): Promise<void> => {
    dispatch(setIsLoading(true))
    dispatch(setError(''))

    try {
        const response = await deleteBaseScheduleApiCall(baseScheduleId)

        if (! isFetchResultSuccessful(response)) {
            dispatch(setError(response.error))

            return
        }

        dispatch(removeBaseScheduleFromIndex(baseScheduleId))
    } catch (error) {
        console.error('[deleteBaseSchedule]', error)
    } finally {
        dispatch(setIsLoading(false))
    }
}

export const addBaseScheduleComment = (
    comment: string,
    baseScheduleId: string,
    dayNumber: number,
) => async (dispatch: TypedDispatch, getState: () => Reducers): Promise<void> => {
    const currentUser = getState().authenticatedUserReducer.user
    const tempId = uuid()

    dispatch(commentAdded({
        id: tempId,
        body: comment,
        owner: currentUser,
        dayNumber,
    }))

    try {
        const response = await postBaseScheduleCommentApiCall(comment, baseScheduleId, dayNumber)

        if (isFetchResultSuccessful(response)) {
            await dispatch(getBaseSchedule(baseScheduleId, false))
        } else {
            dispatch(commentDeleted(tempId))
            dispatch(setError(response.error))
        }
    } catch (error) {
        console.error('[addBaseScheduleComment]', error)
        dispatch(commentDeleted(tempId))
    }
}

export const deleteBaseScheduleComment = (
    commentUuid: string,
) => async (dispatch: TypedDispatch, getState: () => Reducers): Promise<void> => {
    const originalComment = getState().pacoBaseScheduleReducer.baseSchedule?.dayComments.find(comment => comment.id === commentUuid)

    dispatch(commentDeleted(commentUuid))

    try {
        const response = await deleteBaseScheduleCommentApiCall(commentUuid)

        if (! isFetchResultSuccessful(response)) {
            if (originalComment) {
                dispatch(commentAdded(originalComment))
            }
            dispatch(setError(response.error))
        }
    } catch (error) {
        console.error('[deleteBaseScheduleComment]', error)

        if (originalComment) {
            dispatch(commentAdded(originalComment))
        }
    }
}
